import { Box, paletteColors, Tooltip } from '@evaneos/b2b-ui';

import { EditableTrigger } from './EditableTrigger';
import type { EditableTriggerProps } from './EditableTrigger';

type EditableValueProps = Omit<EditableTriggerProps, 'triggerSx' | 'anchorEl'> & {
    /**
     * Optional tooltip to display when the field is disabled
     */
    disabledTooltip?: string;
};

/**
 * Editable value component for use in a DefinitionList.
 * Wraps content in a <dd> tag with a button trigger that opens a popover editor.
 * Use this for a single editable value in a definition list row.
 */
export function EditableValue({
    labelId,
    baseId,
    placement = 'below',
    children,
    editor,
    popoverWidth,
    onBeforeClose,
    disablePopoverMinHeight,
    hoverHint,
    hoverAction,
    showHoverActionOnTouch,
    disabled = false,
    disabledTooltip,
}: EditableValueProps) {
    const content = (
        <EditableTrigger
            labelId={labelId}
            baseId={baseId}
            placement={placement}
            editor={editor}
            popoverWidth={popoverWidth}
            onBeforeClose={onBeforeClose}
            disablePopoverMinHeight={disablePopoverMinHeight}
            hoverHint={hoverHint}
            hoverAction={hoverAction}
            showHoverActionOnTouch={showHoverActionOnTouch}
            disabled={disabled}
            triggerSx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: 0.5,
                minHeight: '3.2rem',
                borderRadius: 1,
                '&:hover': { backgroundColor: paletteColors.neutral[100] },
                '&:focus-visible': { bgcolor: paletteColors.neutral[100] },
            }}
        >
            {children}
        </EditableTrigger>
    );

    return (
        <Box component="dd" sx={{ flex: 1, m: 0 }}>
            {disabled && disabledTooltip ? (
                <Tooltip
                    title={disabledTooltip}
                    placement="top"
                    disableInteractive
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: { xs: '300px', md: 'none' },
                            },
                        },
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -12],
                                    },
                                },
                            ],
                        },
                    }}
                >
                    <Box>{content}</Box>
                </Tooltip>
            ) : (
                content
            )}
        </Box>
    );
}
